/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {Box, Divider, Flex, IconWithText, Text, Theme as ThemeWithoutType} from 'exsportia-components'
import {Formik, useFormikContext} from 'formik'
import {FC, memo, useCallback, useMemo, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {useNavigate, useParams} from 'react-router-dom'

import {BookingQueries} from '../../api/booking'
import {CalculatedBookingPriceObject} from '../../api/booking/calculate-booking-price'
import {GetBookingQueryReturnType} from '../../api/booking/get-booking'
import {isParticipantPayable} from '../../api/booking/helpers'
import {BookingSplitPayRequest} from '../../api/booking/update-pay-split-booking.mutation'
import {GetUserQueryReturnType} from '../../api/user/get-user'
import {useVenue} from '../../api/venue/hooks'
import {CheckoutGateNotice} from '../../components/checkout-gate-notice/checkout-gate-notice'
import {useConfirmationModal} from '../../components/confirmation-modal/confirmation-modal'
import {EmptyList} from '../../components/empty-list/empty-list'
import {ExsportiaPayIcon, PaymentMethodsGroup} from '../../components/payment-icons'
import {PriceRow} from '../../components/price-row/price-row'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useAuthState, useHandleLoginState} from '../../services/auth/hooks'
import {BookingService} from '../../services/booking'
import {BookingStateParticipant} from '../../services/booking/forms/types'
import {useBookingLoader} from '../../services/booking/hooks'
import {useHandleDraftEntityStatusChange} from '../../services/payments/hooks/handle-draft-entity-status-change.hook'
import {usePaymentProvider} from '../../services/payments/hooks/payment-provider-form.provider'
// import {usePaymentProvider} from '../../services/payments/hooks/payment-provider-form.provider'
import {PaymentSubmit, PaymentTypes, usePaymentOptions} from '../../services/payments/hooks/use-payment-options'
import {UserService} from '../../services/user'
import {ThemeType} from '../../theme/types'
import {RadioButton} from '../../ui/components/radio-button'
import {Toggle} from '../../ui/components/toggle'
import {auth} from '../../utils/firebase'
import {useLocale} from '../../utils/i18n'
import {omit} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {BookingServicePricingAndPaymentsSection, BookingServiceSummarySection} from '../booking-summary/booking-service-summary-section'
import {UserPaymentsMethods} from '../set-payment-method/components/user-payments-type-modal'
import {SubmitButton} from '../set-payment-method/set-payment-method'

const Theme = ThemeWithoutType as ThemeType

export type PaySplitPaymentServiceBookingFormType = {
  uid: string
  userId: string
  index?: number
  toPay?: boolean
  discount: number
  entity: 'service'
  memberId?: string
  totalPrice: number
  occupyUsage: boolean
  splitPayment: boolean
  paymentType?: string | null
  useForParticipants: boolean
  paymentMethodId: string | null
  participants: BookingStateParticipant[]
  membership?: BookingStateParticipant['membership']
}

const Header: FC = memo(() => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={l('paymentMethod')} />
})

type PaymentEntityType = 'class' | 'service' | 'subscription'

type UserPaymentTypeModal = {
  type?: PaymentEntityType
  isBookingLoading: boolean
  booking: GetBookingQueryReturnType
}

export const PaySplitPaymentBooking: FC<UserPaymentTypeModal> = ({type = 'service', booking, isBookingLoading}) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {data: venue} = useVenue()
  const [isLoading, setIsLoading] = useState(true)
  const [isSubscriptionAllocable, setIsSubscriptionAllocable] = useState(false)
  const {
    submitForm,
    setValues,
    setFieldValue,
    values: bookingState,
  } = useFormikContext<PaySplitPaymentServiceBookingFormType>()
  const handleSubmitWithPayment = useCallback(
    ({paymentMethodId}: {paymentMethodId: string | null}) => {
      setValues({...bookingState, paymentMethodId, paymentType: PaymentTypes.PAY_NOW})
      submitForm().catch((e) => console.log(String(e)))
    },
    [bookingState, setValues, submitForm],
  )
  const {data: bookingPriceData} = useQuery(
    BookingQueries.calculateSplitBookingPriceQuery({enabled: false, refetchOnWindowFocus: false}),
  )
  const total = useMemo(
    () => (bookingPriceData ? Number(bookingState?.splitPayment ? bookingPriceData.toPay : bookingPriceData.price) : 0),
    [bookingPriceData, bookingState],
  )
  const handleClickButton: PaymentSubmit = useCallback(
    ({paymentType, paymentMethodToken}) => {
      if (paymentType === PaymentTypes.PAY_NOW && total > 0) {
        return handleSubmitWithPayment({paymentMethodId: paymentMethodToken})
      }
      if (paymentType === PaymentTypes.ONE_TIME && total > 0) {
        setValues({...bookingState, paymentType: PaymentTypes.ONE_TIME})
        submitForm().catch((e) => console.log(String(e)))
        return
      }
      setValues({...bookingState, paymentType: PaymentTypes.PAY_ON_LOCATION})
      submitForm().catch((e) => console.log(String(e)))
    },
    [total, submitForm, bookingState, handleSubmitWithPayment],
  )
  const {
    paymentType,
    allowedOptions,
    setPaymentType,
    submitWithPayment,
    paymentMethodToken,
    setUserPaymentToken,
    checkoutGate,
    isCheckingCharge,
  } =
    usePaymentOptions({
      submit: handleClickButton,
      // @ts-ignore
      entity: booking.sport, // TODO: fix type
      redirectLoggedOut: false,
      entityType: type,
      totalPrice: bookingPriceData ? total : undefined,
    })
  const {isLoggedIn} = useAuthState()
  const [loggedInState, setLoggedInState] = useState(isLoggedIn)
  const handleLogOut = useCallback(() => {
    if (loggedInState) {
      navigate(-2)
      setValues({...bookingState, participants: []})
    }
    setLoggedInState(isLoggedIn)
  }, [bookingState])
  useHandleLoginState({
    handleLogOut,
  })
  const navigateBackToProfile = useCallback(() => {
    navigate('../')
  }, [])
  const setOneTimePayment = useCallback(() => setPaymentType(PaymentTypes.ONE_TIME), [])
  const handleSetIsApplySubscriptionForParticipants = useCallback(
    (value: boolean) => {
      setFieldValue('useForParticipants', value)
    },
    [bookingState],
  )

  return (
    <GridLayout header={<Header />}>
      {bookingState?.participants?.length === 0 && !isLoading ? (
        <EmptyList
          onClick={navigateBackToProfile}
          text={l('splitBookingWarningAllPaid')}
          buttonText={l('back')}
        />
      ) : (
        <Box>
          <BookingServicePricingAndPaymentsSection
            setIsSubscriptionAllocable={setIsSubscriptionAllocable}
            booking={booking}
            setIsLoading={setIsLoading}
          />
          {bookingState.participants.length > 1 && bookingState.splitPayment && (
            <Flex
              justifyContent='space-between'
              py='12px'
            >
              <IconWithText
                text={l('split_payment_among_participants', {caseAction: 'capitalize'})}
                icon='paymentSplit'
              />
              <Toggle
                styleType='small'
                disabled={true}
                checked={bookingState.splitPayment}
                onChange={() => {
                  setValues({
                    ...bookingState,
                    splitPayment: !bookingState?.splitPayment,
                    participants: bookingState?.participants?.map((participant, index) => {
                      if (index === 0) {
                        return {
                          ...participant,
                          toPay: true,
                        }
                      }
                      return {
                        ...participant,
                        toPay: false,
                      }
                    }),
                  })
                }}
              />
            </Flex>
          )}
          {isSubscriptionAllocable && bookingState.participants.length > 1 && (
            <Flex
              justifyContent='space-between'
              py='12px'
            >
              <Text>{l('apply_subscription_to_all_participants', {caseAction: 'capitalize'})}</Text>
              <Toggle
                styleType='small'
                checked={bookingState.useForParticipants}
                onChange={handleSetIsApplySubscriptionForParticipants}
              />
            </Flex>
          )}
          {/* This page offers no pay-on-location row, so never point the payer at "another method below". */}
          <CheckoutGateNotice gate={{...checkoutGate, hasOtherMethod: false}} />
          {total > 0 && (
            <Box mb='16px'>
              <Divider />
              {allowedOptions.includes(PaymentTypes.ONE_TIME) && (
                <Flex
                  alignItems='center'
                  height={Theme?.spaces?.multiplier6x}
                >
                  <RadioButton
                    disabled={isBookingLoading || checkoutGate.isGated}
                    checked={paymentType === PaymentTypes.ONE_TIME}
                    onChange={setOneTimePayment}
                    text={l('exsportiaPay', {caseAction: 'capitalize'})}
                    iconBefore={<ExsportiaPayIcon w={40} h={24} />}
                    iconAfter={<Box ml='auto'><PaymentMethodsGroup w={180} h={32} /></Box>}
                  />
                </Flex>
              )}
              <Divider />
              {allowedOptions.includes(PaymentTypes.PAY_NOW) && (
                <>
                  <Box>
                    <UserPaymentsMethods
                      isLoading={isBookingLoading || checkoutGate.isGated}
                      venueId={venue?.uid}
                      paymentMethod={paymentMethodToken}
                      setPaymentMethod={setUserPaymentToken}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
          <Flex my='16px'>
            <PriceRow
              bold
              text={l('toPay')}
              currency={venue?.currency || ''}
              price={total}
            />
          </Flex>
          <SubmitButton
            totalPrice={total}
            paymentType={paymentType}
            isLoading={isBookingLoading || isLoading || isCheckingCharge}
            onClick={submitWithPayment}
            disabled={checkoutGate.isSelectionBlocked}
          />
        </Box>
      )}
      <Box>
        <BookingServiceSummarySection
          values={bookingState}
          booking={booking}
          isLoading={!bookingState}
        />
      </Box>
    </GridLayout>
  )
}

type dataForAdapterType = {
  venueId: string
  user?: GetUserQueryReturnType,
  booking: GetBookingQueryReturnType
  priceDataObject: CalculatedBookingPriceObject,
  bookingState: PaySplitPaymentServiceBookingFormType
}

const commonServiceBookingPayByTokenAdapter = (dataForAdapter: dataForAdapterType) => {
  const {
    user,
    venueId,
    booking,
    bookingState,
    priceDataObject,
  } = dataForAdapter
  const currentUserParticipantIndex = (bookingState?.participants || []).findIndex((p) => {
    return p.toPay
  })
  const participantsData = (booking?.participants || []).map((el, index) => {
    const priceDataParticipants = priceDataObject?.participants.find((p) => p.index === index)
    const formStateParticipantIndex = bookingState.participants.findIndex((p) => p.index === index)
    const formStateParticipants = bookingState.participants.find((p) => p.index === index)
    if (!formStateParticipants?.toPay) {
      return omit(el, ['toPay'])
    }
    const discount = priceDataParticipants?.totalDiscount || 0
    const usedMinutesWithSub = priceDataParticipants?.limitationCheckResult?.used.minutes ?? null
    const usedCreditsWithSub = priceDataParticipants?.limitationCheckResult?.used.credits ?? null
    const usedEntriesWithSub = priceDataParticipants?.limitationCheckResult?.used.entries ?? null
    const userData =
      formStateParticipantIndex === currentUserParticipantIndex
        ? {
            userId: user?.uid || '',
            name: formStateParticipants?.name || el?.name,
          }
        : {}

    return {
      ...el,
      ...userData,
      discount,
      usedMinutesWithSub,
      usedCreditsWithSub,
      usedEntriesWithSub,
      totalDiscount: discount,
      toPay: formStateParticipants?.toPay || false,
      isPaid: priceDataParticipants?.isPaid || false,
      totalPrice: priceDataParticipants?.totalPrice || 0,
      activityPrice: priceDataParticipants?.activityPrice || 0,
      activityDiscount: priceDataParticipants?.activityDiscount || 0,
      activityTotalPrice: priceDataParticipants?.activityTotalPrice || 0,
      activityInitialDiscount: priceDataParticipants?.activityInitialDiscount || 0,
      equipmentsPrice: priceDataParticipants?.equipmentsPrice || 0,
      equipmentsDiscount: priceDataParticipants?.equipmentsDiscount || 0,
      equipmentsTotalPrice: priceDataParticipants?.equipmentsTotalPrice || 0,
      equipmentsInitialDiscount: priceDataParticipants?.equipmentsInitialDiscount || 0,
      totalInitialDiscount: priceDataParticipants?.totalInitialDiscount || 0,
      membershipName: priceDataParticipants?.membershipName || null,
      membershipId: priceDataParticipants?.membershipId || null,
      activityGroupId: priceDataParticipants?.activityGroupId || null,
      initialDiscount: priceDataParticipants?.totalInitialDiscount || 0,
      equipmentDiscount: priceDataParticipants?.equipmentsTotalPrice || 0,
      membershipBookingId: priceDataParticipants?.membershipBookingId || null,
      occupyUsage: discount > 0 || (discount === 0 && priceDataParticipants?.totalPrice === 0),
    }
  })

  return {
    uid: dataForAdapter.bookingState.uid,
    venueId,
    bookingId: dataForAdapter.bookingState.uid,
    sum: dataForAdapter?.priceDataObject?.toPay || 0,
    paymentMethodId: dataForAdapter.bookingState.paymentMethodId,
    data: {
      venueId,
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      userId: user?.uid || '',
      participants: participantsData,
    },
  }
}

type Params = {
  venueId: string
  bookingId: string
}

export const PayBookingForm: FC = () => {
  // const luxon = useLuxon()
  const navigate = useNavigate()
  const [entityId, setEntityId] = useState<string | null>(null)
  const {isLoading, setIsLoading} = useBookingLoader()
  const {venueId, bookingId} = useParams<Params>()
  const {closeAllModals} = useModalActions()
  const {
    data: booking,
    isLoading: isBookingLoading,
    // refetch,
  } = useQuery(BookingService.queries.getBookingQuery({params: {id: bookingId}, cacheTime: 0}))
  const initialValues = useMemo<PaySplitPaymentServiceBookingFormType>(
    () => ({
      uid: booking?.uid || '',
      entity: 'service',
      userId: booking?.userId || '',
      discount: booking?.discount ?? 0,
      useForParticipants: false,
      totalPrice: booking?.totalPrice ?? 0,
      paymentType: booking?.paymentType,
      occupyUsage: booking?.usageData?.amountByType === 'AMOUNT_BY_ENTRIES',
      splitPayment: booking?.splitPayment === true,
      participants: (booking?.participants ?? [])
        .map((el, index) => ({
          index,
          avatar: el.avatar,
          isPaid: el.isPaid,
          name: el.name || '',
          totalPrice: el.totalPrice,
          activityPrice: el.activityPrice,
          equipmentsPrice: el.equipmentsPrice,
          equipments: el.equipments,
          membershipId: el.membershipId,
          initialDiscount: el.initialDiscount,
          membershipBookingId: el.membershipBookingId,
          equipmentsTotalPrice: el.equipmentsTotalPrice,
        }))
        .filter(isParticipantPayable)
        .map((el, index) => ({
          ...el,
          toPay: index === 0,
        })),
      paymentMethodId: null,
    }),
    [venueId, booking],
  )
  const {mutate: updateServiceBookingPayByToken, isLoading: isLoadingPayByToken} = useMutation(
    BookingService.mutations.updateServiceBookingSplitPayByTokenMutation({}),
  )
  const {openConfirmationModal} = useConfirmationModal()
  const {mutate: checkIfBookingParticipantsNotPay, isLoading: isLoadingCheckPayParts} = useMutation(
    BookingService.mutations.checkSplitBookingPaymentPartsMutation({}),
  )
  const {mutate: updateServiceBookingPaySubscription, isLoading: isLoadingPaySubscription} = useMutation(
    BookingService.mutations.updateServiceBookingSplitPaySubscriptionMutation({}),
  )
  const {mutate: createDraftSplitBooking, isLoading: isLoadingCreateSplitPayment} = useMutation(
    BookingService.mutations.createSplitPaymentBookingDraftMutation({}),
  )
  const {setStartRefetch, removeDraftEntity} = useHandleDraftEntityStatusChange({
    entity: 'booking-split',
    entityId,
    isLoading,
    setIsLoading,
  })
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const {data: priceDataObject, isLoading: isLoadingCalculatePrice} = useQuery(
    BookingQueries.calculateSplitBookingPriceQuery({enabled: false, refetchOnWindowFocus: false}),
  )
  const {openPaymentForm} = usePaymentProvider()
  const handleSubmit = (values: PaySplitPaymentServiceBookingFormType) => {
    if (!priceDataObject || !booking) {
      return
    }
    const updateSubscription =
      (priceDataObject.toPay || 0) > 0 ? updateServiceBookingPayByToken : updateServiceBookingPaySubscription
    if (!venueId) {
      return
    }
    const dataForAdapter = {
      venueId,
      user,
      booking,
      bookingState: {
        ...values,
      },
      priceDataObject,
    }
    const adaptedData: BookingSplitPayRequest = commonServiceBookingPayByTokenAdapter(dataForAdapter)
    const dataForDraft = adaptedData

    const callWhenHavePaidParticipantsParts = () => {
      openConfirmationModal({
        description: 'paySplitBookingWarningOnPayment',
        onConfirm: () => {
          window.location.reload()
        },
      })
    }

    const callAfterCheckPaymentParticipants = () => {
      if (values.paymentType === 'oneTimePayment') {
        if (dataForDraft === null) {
          return
        }
        const dataForDraftToUse = dataForDraft
        const participantToPayIndex = dataForDraftToUse.data.participants.findIndex((participant) => participant.toPay)
        createDraftSplitBooking(adaptedData, {
          onSuccess: () => {
            removeDraftEntity()
            setEntityId(dataForDraftToUse.bookingId || null)
            setStartRefetch(true)
            openPaymentForm?.({
              entity: 'booking-split',
              participantToPayIndex,
              totalPrice: dataForDraftToUse.sum,
              bookingId: dataForDraftToUse.uid,
              startProcessCallback: () => setIsLoading(true),
            })
          },
        })
      } else {
        setEntityId(null)
        setIsLoading(true)
        updateSubscription(adaptedData, {
          onSuccess: (resBooking) => {
            closeAllModals()
            setIsLoading(false)
            const res = resBooking as { uid?: string; bookingId?: string }
            const bookingUid = res?.uid ?? res?.bookingId ?? booking?.uid
            if (venueId && bookingUid) {
              navigate(`/${venueId}/bookings/${bookingUid}`, {replace: true})
            }
          },
          onError: (error) => {
            console.log(error)
            setIsLoading(false)
            if (error.response?.status === 409) {
              // return openBookingValidationModal({type: 'service'})
            }
          },
        })
      }
    }

    checkIfBookingParticipantsNotPay({
      callAfterErrorCheck: callWhenHavePaidParticipantsParts,
      callAfterSuccessCheck: callAfterCheckPaymentParticipants,
      ...dataForDraft,
    })
  }

  if (!booking || isBookingLoading) {
    return null
  }
  const isLoadingSplitPaymentFlow =
    isLoading ||
    isLoadingCheckPayParts ||
    isBookingLoading ||
    isLoadingCalculatePrice ||
    isLoadingPayByToken ||
    isLoadingPaySubscription ||
    isLoadingCreateSplitPayment

  return (
    <Formik
      onSubmit={handleSubmit}
      validateOnChange={false}
      initialValues={initialValues}
    >
      <PaySplitPaymentBooking
        booking={booking}
        isBookingLoading={isLoadingSplitPaymentFlow}
      />
    </Formik>
  )
}
